import { Box, Divider, Drawer, IconButton, Typography } from '@mui/material';
import * as Icon from '@mui/icons-material/';
import { ConteudoAjuda } from './tipos';
import { telasComAjuda } from './conteudo';

interface AjudaDrawerProps {
  aberto: boolean;
  fechar: () => void;
  conteudo: ConteudoAjuda | null;
  carregando: boolean;
  /** Esta tela ainda não tem texto escrito. Não é erro - é o estado normal hoje. */
  semAjuda: boolean;
}

/**
 * Painel lateral de ajuda. Drawer, e não modal, de propósito: a pessoa consegue ler o passo a
 * passo com a tela ainda visível ao lado, em vez de decorar e fechar.
 *
 * O Esc fecha (comportamento nativo do Drawer do MUI) e o clique fora também.
 *
 * [AJUSTE 21/08/2026] Dois estilos globais entravam aqui e atrapalhavam a leitura:
 *
 *   1. `text-transform: uppercase` no body (src/index.css) deixava o painel INTEIRO em caixa
 *      alta. Aceitável em rótulo de coluna; ruim em parágrafo de instrução, que é justamente
 *      o que este painel é. Escapamos com textTransform none no Paper - mesmo recurso que a
 *      ContasReceberPesquisar já usa duas vezes.
 *   2. O preflight do Tailwind zera `list-style` de ol/ul, e os passos perdiam a numeração -
 *      num "passo 1, passo 2" isso é o conteúdo, não enfeite. Restaurado aqui.
 *
 * Os dois consertos são ESCOPADOS ao painel. Mexer no CSS global para arrumar uma tela
 * mudaria as outras 84 de tabela.
 */
export const AjudaDrawer = ({
  aberto,
  fechar,
  conteudo,
  carregando,
  semAjuda,
}: AjudaDrawerProps) => (
  <Drawer
    anchor="right"
    open={aberto}
    onClose={fechar}
    PaperProps={{
      sx: {
        width: { xs: '100%', sm: 460 },
        px: 3,
        pb: 3,
        textTransform: 'none',
      },
    }}
  >
    {/* Cabeçalho fixo: num painel que rola, o X tem de continuar alcançável. */}
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
        pt: 3,
        pb: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
      }}
    >
      <Icon.HelpOutline sx={{ mt: '2px' }} />
      <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
        {conteudo?.titulo ?? 'Ajuda'}
      </Typography>
      <IconButton onClick={fechar} size="small" aria-label="Fechar a ajuda">
        <Icon.Close />
      </IconButton>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {carregando && <Typography variant="body2">Carregando…</Typography>}

    {/*
      Tela sem texto escrito. Dizer isso com franqueza, e listar o que já existe, é melhor do
      que esconder o botão: com a cobertura baixa, botão ausente é indistinguível de defeito -
      foi o que aconteceu na tela de Contas a Pagar em 21/08.
    */}
    {!carregando && semAjuda && (
      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          A ajuda desta tela <strong>ainda não foi escrita</strong>.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Estamos escrevendo aos poucos, começando pelas telas mais usadas. Por enquanto
          existe em:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3, listStyle: 'disc', listStylePosition: 'outside' }}>
          {telasComAjuda().map((nome) => (
            <Typography key={nome} component="li" variant="body2" sx={{ display: 'list-item' }}>
              {nome}
            </Typography>
          ))}
        </Box>
      </>
    )}

    {!carregando && !semAjuda && !conteudo && (
      <Typography variant="body2">
        Não foi possível carregar a ajuda desta tela. Tente novamente.
      </Typography>
    )}

    {!carregando && conteudo && (
      <>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {conteudo.oQueEstaTelaFaz}
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          COMO FAZER
        </Typography>

        {conteudo.comoFazer.map((item) => (
          <Box key={item.tarefa} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.tarefa}
            </Typography>
            <Box
              component="ol"
              sx={{ m: 0, pl: 3, listStyle: 'decimal', listStylePosition: 'outside' }}
            >
              {item.passos.map((passo, i) => (
                <Typography
                  key={i}
                  component="li"
                  variant="body2"
                  sx={{ mb: 0.5, display: 'list-item' }}
                >
                  {passo}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}

        {conteudo.seAlgoNaoAparecer && conteudo.seAlgoNaoAparecer.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              SE ALGO NÃO APARECER
            </Typography>
            <Box
              component="ul"
              sx={{ m: 0, pl: 3, listStyle: 'disc', listStylePosition: 'outside' }}
            >
              {conteudo.seAlgoNaoAparecer.map((item, i) => (
                <Typography
                  key={i}
                  component="li"
                  variant="body2"
                  sx={{ mb: 1, display: 'list-item' }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Atalho: <strong>?</strong> abre e fecha esta ajuda. Esc fecha.
        </Typography>
      </>
    )}
  </Drawer>
);
